import { Container, PortalLayout, RenewalSummaryCard, Stack, Typography } from "../../design-system";
import { formatLongDate } from "../../content/format-long-date";
import { formatPolicyLine } from "../../content/policy-line";
import type { PolicyInScope } from "../../state/renewal-summary-port";
import { renewalPreviewContent as content } from "./content";

/**
 * Pre-renewal window preview route — Screen 8 (AC-23, resolving
 * DEBT-20261015-p9rw). Shown instead of every other journey screen when
 * the renewal data port reports the renewal window as not yet open
 * (NFR-gag78j: this component states facts read through the port only —
 * no suggestion, no recommendation, no action). No stepper, no buttons:
 * there is nothing for the customer to do here.
 *
 * Reuses the shared `RenewalSummaryCard` head H2 explicitly calls "the
 * same card as screen 7's head" — never a second, independently-built
 * copy of the eyebrow/business-name/calendar/shield pattern.
 *
 * Both `isRenewalWindowOpen` (which route to show at all — the caller's
 * concern) and `windowOpensOn` (the merge-field date below) are read
 * through the renewal data port; this component performs no date
 * arithmetic of its own ("150 days before renewal date" is FRP's process,
 * computed server-side).
 */
export interface RenewalPreviewRouteProps {
  businessName: string;
  /** ISO 8601 (YYYY-MM-DD). */
  renewalDate: string;
  policiesInScope: readonly PolicyInScope[];
  /** ISO 8601 (YYYY-MM-DD) — the date the outreach email will be sent,
   * read through the port (AC-23), never calculated here. */
  windowOpensOn: string;
}

export function RenewalPreviewRoute({
  businessName,
  renewalDate,
  policiesInScope,
  windowOpensOn,
}: Readonly<RenewalPreviewRouteProps>) {
  return (
    <PortalLayout businessName={businessName}>
      <Container layout="summary">
        <RenewalSummaryCard
          businessName={businessName}
          metaRows={[
            {
              icon: "calendar",
              content: (
                <>
                  Renews{" "}
                  <Typography variant="body" as="time" dateTime={renewalDate}>
                    {formatLongDate(renewalDate)}
                  </Typography>
                </>
              ),
            },
            { icon: "shield", content: formatPolicyLine(policiesInScope) },
          ]}
        >
          <Stack gap="lg">
            <Typography variant="bodyLarge">
              <strong>{content.noActionNeeded}</strong>
            </Typography>
            <Typography variant="bodyLarge">
              {content.emailNoticePrefix}
              <Typography variant="bodyLarge" as="time" dateTime={windowOpensOn}>
                {formatLongDate(windowOpensOn)}
              </Typography>
              {content.emailNoticeSuffix}
            </Typography>
          </Stack>
        </RenewalSummaryCard>
      </Container>
    </PortalLayout>
  );
}
